import React, { useEffect, useState } from "react";
import { Modal, Form, Button, Dropdown, Tag, Switch, message, Input, InputNumber } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, PoweroffOutlined, MoreOutlined } from "@ant-design/icons";

import {
  getSubscriptionPlansApi,
  createSubscriptionPlanApi,
  updateSubscriptionPlanApi,
  deleteSubscriptionPlanApi,
} from "../../services/api";
import AppTable from "../../common/AppTable";
import AppInput from "../../common/FormInputs/AppInput";

const ACCENT = "#534ab7";

export default function SubscriptionPlans() {
  const [plans,   setPlans]   = useState([]);
  const [open,     setOpen]     = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [form] = Form.useForm();

  const fetchPlans = async () => {
    try {
      const res = await getSubscriptionPlansApi();
      setPlans((res.data.data || []).slice().sort((a, b) => a.sortOrder - b.sortOrder));
    } catch {
      message.error("Failed to load subscription plans");
    }
  };

  useEffect(() => { fetchPlans(); }, []);

  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ active: true, isDefault: false, sortOrder: 0, color: "#534ab7" });
    setOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      name:      record.name,
      price:     record.price,
      period:    record.period,
      icon:      record.icon,
      color:     record.color,
      features:  (record.features || []).join("\n"),
      isDefault: record.isDefault,
      sortOrder: record.sortOrder,
      active:    record.active,
    });
    setOpen(true);
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    setLoading(true);
    try {
      const payload = {
        ...values,
        features: (values.features || "").split("\n").map(f => f.trim()).filter(Boolean),
      };
      if (editing) {
        await updateSubscriptionPlanApi(editing.id, payload);
        message.success("Plan updated");
      } else {
        await createSubscriptionPlanApi(payload);
        message.success("Plan added");
      }
      setOpen(false);
      fetchPlans();
    } catch (err) {
      message.error(err?.response?.data?.data || "Failed to save plan");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (record) => {
    try {
      await updateSubscriptionPlanApi(record.id, { ...record, active: !record.active });
      message.success(record.active ? "Plan deactivated" : "Plan activated");
      fetchPlans();
    } catch {
      message.error("Failed to update status");
    }
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: "Delete this plan?",
      content: "It will be removed from the mobile app's subscription screen.",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteSubscriptionPlanApi(id);
          message.success("Plan deleted");
          fetchPlans();
        } catch (err) {
          message.error(err?.response?.data?.data || "Failed to delete plan");
        }
      },
    });
  };

  const columns = [
    {
      title: "Plan",
      dataIndex: "name",
      render: (v, r) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 20 }}>{r.icon}</span>
          <div>
            <div style={{ fontWeight: 600, color: "#111827" }}>{v}</div>
            <div style={{ fontSize: 11, color: "#9CA3AF" }}>{r.price} · {r.period}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Features",
      dataIndex: "features",
      render: (v) => <span style={{ fontSize: 12, color: "#6B7280" }}>{(v || []).length} features</span>,
    },
    {
      title: "Sort",
      dataIndex: "sortOrder",
      width: 70,
      align: "center",
    },
    {
      title: "Default",
      dataIndex: "isDefault",
      width: 90,
      align: "center",
      render: (v) => v && <Tag color="blue">Default</Tag>,
    },
    {
      title: "Status",
      dataIndex: "active",
      width: 90,
      align: "center",
      render: (v) => <Tag color={v ? "success" : "default"}>{v ? "Active" : "Off"}</Tag>,
    },
    {
      title: "Actions",
      width: 70,
      align: "center",
      render: (_, r) => (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              { key: "edit", label: "Edit", icon: <EditOutlined />, onClick: () => openEdit(r) },
              {
                key: "toggle",
                label: r.active ? "Deactivate" : "Activate",
                icon: <PoweroffOutlined />,
                onClick: () => handleToggleActive(r),
              },
              { type: "divider" },
              { key: "delete", label: "Delete", icon: <DeleteOutlined />, danger: true, onClick: () => handleDelete(r.id) },
            ],
          }}
        >
          <Button icon={<MoreOutlined />} size="small" />
        </Dropdown>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Subscription Plans</div>
          <div className="page-sub">{plans.length} plans — shown on the mobile app's "Current Plan" screen</div>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          style={{ background: ACCENT, border: "none", borderRadius: 8 }}
          onClick={openAdd}
        >
          Add Plan
        </Button>
      </div>

      <AppTable columns={columns} data={plans} />

      <Modal
        title={editing ? `Edit — ${editing.name}` : "Add Plan"}
        open={open}
        width={520}
        okText={editing ? "Save Changes" : "Add Plan"}
        okButtonProps={{ style: { background: ACCENT, border: "none" }, loading }}
        onOk={handleSubmit}
        onCancel={() => { setOpen(false); setEditing(null); form.resetFields(); }}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          <AppInput
            name="name"
            label="Plan Name"
            rules={[{ required: true, message: "Plan name is required" }]}
            placeholder="e.g. Premium"
          />

          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <AppInput name="price" label="Price" rules={[{ required: true }]} placeholder="e.g. ₹299" />
            </div>
            <div style={{ flex: 1 }}>
              <AppInput name="period" label="Period" rules={[{ required: true }]} placeholder="e.g. per month" />
            </div>
          </div>

          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <AppInput name="icon" label="Icon Emoji" placeholder="e.g. ⭐" />
            </div>
            <div style={{ flex: 1 }}>
              <AppInput name="color" label="Accent Color (hex)" placeholder="#FFA726" />
            </div>
          </div>

          <Form.Item name="features" label="Features (one per line)">
            <Input.TextArea rows={5} placeholder={"Everything in Free\nUnlimited child profiles\nPriority support"} />
          </Form.Item>

          <div style={{ display: "flex", gap: 24, alignItems: "flex-start" }}>
            <Form.Item name="sortOrder" label="Sort Order" style={{ width: 120 }}>
              <InputNumber min={0} max={999} style={{ width: "100%", borderRadius: 8 }} />
            </Form.Item>
            <Form.Item name="isDefault" label="Default Plan" valuePropName="checked">
              <Switch checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>
            <Form.Item name="active" label="Active" valuePropName="checked">
              <Switch checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
